import {
  Component,
  OnInit,
  AfterViewInit,
  OnDestroy,
  Input,
  Output,
  EventEmitter,
  ViewChild,
  ElementRef,
  TemplateRef,
} from '@angular/core';
import SignaturePad from 'signature_pad';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { MessageService } from 'primeng/api';
import * as bootstrap from 'bootstrap';
import { ESignService } from 'src/app/services/e-sign.service';
import { ImageGeneratorService } from 'src/app/services/image-generator.service';
import { UrlValidatorService } from 'src/app/services/url-validator.service';
import { DomainType } from 'src/app/enum/domain-type.enum';
import { NgxSpinnerService } from 'ngx-spinner';

@Component({
  selector: 'app-electronic-sign',
  templateUrl: './electronic-sign.component.html',
  styleUrls: ['./electronic-sign.component.scss'],
  standalone: false,
})
export class ElectronicSignComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  // ─── Inputs from Parent ────────────────────────────────────────────────────
  @Input() signatoryDetails: any = {};
  @Input() orderId: string;
  @Input() esignId: string;
  @Input() signatoryId: string;
  @Input() positionDetails: any = {};
  @Input() userData: any;
  @Input() webcamImage: any = null;
  @Input() enableCaptureImage: boolean = true;
  @Input() isChecked: boolean = false;
  @Input() orderType: any;
  @Input() base64Data: string;
  @Input() signPosition: string = '';

  // ─── Outputs to Parent ─────────────────────────────────────────────────────
  /** Emits when signing is successfully submitted so parent can refresh */
  @Output() signCompleted = new EventEmitter<void>();
  /** Emits the captured webcam image up to parent so other children can share it */
  @Output() webcamImageChange = new EventEmitter<any>();
  /** Emits the signImage so parent can display it in right panel if needed */
  @Output() signImageChange = new EventEmitter<any>();

  // ─── ViewChildren ──────────────────────────────────────────────────────────
  @ViewChild('signPadCanvas', { static: false })
  signaturePadElement: ElementRef;
  @ViewChild('signatureWarning') signatureWarningTemplate: TemplateRef<any>;

  // ─── Signature State ───────────────────────────────────────────────────────
  signPad: any;
  signImage: any = null;
  /** signImage padded to the signature box's shape; this is what gets submitted. */
  stampImage: string = '';
  signOption: string = 'select';
  imageStyle: string | ArrayBuffer;
  isSignatureValid: boolean = false;
  selectedStyle: string = "italic 70px 'Brush Script MT', cursive";
  selectedName: string;
  signatoryName: string;
  pdfSource: any; // used for upload preview
  selectedFile: string;
  fileName: string = 'Upload Image';

  redirecturl: string = '';

  isEmailButtonDisabled: boolean = false;
  emailCooldownTime: number = 0;
  private emailCooldownInterval: any;

  // ─── Font Styles ───────────────────────────────────────────────────────────
  signStyle = [
    { name: 'Comic Sans', code: "italic 70px 'Comic Sans MS', cursive" },
    { name: 'Brush Script', code: "italic 70px 'Brush Script MT', cursive" },
    {
      name: 'Lucida Calligraphy',
      code: "italic 70px 'Lucida Calligraphy', cursive",
    },
    { name: 'Apple Chancery', code: "italic 70px 'Apple Chancery', cursive" },
    { name: 'Arial', code: 'italic 70px Arial, sans-serif' },
    { name: 'Times New Roman', code: "italic 70px 'Times New Roman', serif" },
    { name: 'Cambria', code: 'italic 70px Cambria, serif' },
    { name: 'Fantasy', code: 'italic 70px Fantasy, sans-serif' },
    { name: 'Monospace', code: "italic 70px 'Courier New', monospace" },
    { name: 'Verdana', code: 'italic 70px Verdana, sans-serif' },
    { name: 'Georgia', code: 'italic 70px Georgia, serif' },
    { name: 'Helvetica', code: 'italic 70px Helvetica, sans-serif' },
    { name: 'Impact', code: 'italic 70px Impact, sans-serif' },
    { name: 'Lucida Console', code: "italic 70px 'Lucida Console', monospace" },
    { name: 'Palatino', code: "italic 70px 'Palatino Linotype', serif" },
    { name: 'Courier', code: "italic 70px 'Courier New', monospace" },
    { name: 'Garamond', code: "italic 70px 'Adobe Garamond Pro', serif" },
    { name: 'Bookman', code: "italic 70px 'Bookman Old Style', serif" },
  ];

  constructor(
    private spinner: NgxSpinnerService,
    private modalService: NgbModal,
    private eSignService: ESignService,
    private imageGeneratorService: ImageGeneratorService,
    private messageService: MessageService,
    private urlValidator: UrlValidatorService,
  ) {}

  ngOnInit(): void {
    // Pre-generate signature style once signatoryDetails is available
    if (this.signatoryDetails?.name) {
      this.signatoryName = this.signatoryDetails.name;
      this.selectTextSign('Name', 'Brush Script MT');
    }
  }

  ngAfterViewInit(): void {
    if (this.signaturePadElement) {
      this.signPad = new SignaturePad(this.signaturePadElement.nativeElement);
    }
  }

  ngOnDestroy(): void {
    if (this.emailCooldownInterval) {
      clearInterval(this.emailCooldownInterval);
    }
  }

  // ─── ngOnChanges: re-generate when signatoryDetails arrives ────────────────
  ngOnChanges(): void {
    if (this.signatoryDetails?.name && !this.signatoryName) {
      this.signatoryName = this.signatoryDetails.name;
      this.selectTextSign('Name', 'Brush Script MT');
    }
  }

  // ──────────────────────────────────────────────────────────────────────────
  // SIGNATURE PAD
  // ──────────────────────────────────────────────────────────────────────────

  initSignaturePad(): void {
    setTimeout(() => {
      const canvas = document.getElementById(
        'signPadCanvasPortal',
      ) as HTMLCanvasElement;
      if (canvas) {
        this.signPad = new SignaturePad(canvas);
      }
    }, 100);
  }

  undoSign(): void {
    const data = this.signPad.toData();
    if (data) {
      data.pop();
      this.signPad.fromData(data);
    }
  }

  clearSignPad(): void {
    this.signPad.clear();
  }

  // ──────────────────────────────────────────────────────────────────────────
  // TEXT / STYLE SIGNATURE
  // ──────────────────────────────────────────────────────────────────────────

  selectTextSign(value: string, style: string): void {
    this.selectedStyle = 'italic 70px ' + style + ', cursive';
    if (value === 'Name') {
      this.selectedName = this.signatoryDetails?.name || this.signatoryName;
      if (this.selectedName && this.selectedName.trim() !== '') {
        this.imageStyle = this.generateWrappedTextImage(
          this.selectedName,
          this.selectedStyle,
        );
      } else {
        this.imageStyle = null;
      }
    }
  }

  onNameChange(): void {
    this.signatoryDetails.name = this.signatoryName;
    this.selectedName = this.signatoryName;
    if (this.signOption === 'select' && this.selectedStyle) {
      this.imageStyle = this.generateWrappedTextImage(
        this.signatoryName,
        this.selectedStyle,
      );
    }
  }

  getStyleType(): void {
    this.signatoryDetails.name = this.signatoryName;
    this.selectedName = this.signatoryName;
    if (this.signOption === 'select' && this.selectedStyle) {
      this.imageStyle = this.generateWrappedTextImage(
        this.signatoryName,
        this.selectedStyle,
      );
    }
  }

  calculateOptimalTextStyle(text: string, originalStyle: string): string {
    const fontFamilyMatch = originalStyle
      ? originalStyle.match(/(?:italic\s+)?(?:\d+px\s+)?(.+)/)
      : null;
    const fontFamily = fontFamilyMatch?.[1] ?? 'cursive';
    const len = text.length;
    const fontSize =
      len <= 10 ? 60 : len <= 15 ? 50 : len <= 20 ? 45 : len <= 25 ? 35 : 25;
    return `italic ${fontSize}px ${fontFamily}`;
  }

  generateWrappedTextImage(text: string, style: string): string {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return '';
    canvas.width = 380;
    canvas.height = 80;
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    const fontFamilyMatch = style
      ? style.match(/(?:italic\s+)?(?:\d+px\s+)?(.+)/)
      : null;
    const fontFamily = fontFamilyMatch?.[1] ?? 'cursive';

    ctx.fillStyle = 'black';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';
    const padding = 10;
    const maxWidth = canvas.width - padding * 2;
    const maxHeight = canvas.height - padding * 2;
    let fontSize =
      text.length < 15
        ? 65
        : text.length < 20
          ? 55
          : text.length < 25
            ? 48
            : text.length < 30
              ? 42
              : 35;
    ctx.font = `italic ${fontSize}px ${fontFamily}`;

    const wrapText = (t: string, mw: number): string[] => {
      const words = t.split(' ');
      const lines: string[] = [];
      let currentLine = words[0];
      for (let i = 1; i < words.length; i++) {
        const testLine = currentLine + ' ' + words[i];
        if (ctx.measureText(testLine).width > mw || testLine.length > 25) {
          lines.push(currentLine);
          currentLine = words[i];
        } else {
          currentLine = testLine;
        }
      }
      lines.push(currentLine);
      return lines;
    };

    let lines = wrapText(text, maxWidth);
    let lineHeight = fontSize * 1.2;
    let totalHeight = lines.length * lineHeight;
    while (totalHeight > maxHeight && fontSize > 12) {
      fontSize -= 2;
      ctx.font = `italic ${fontSize}px ${fontFamily}`;
      lineHeight = fontSize * 1.2;
      lines = wrapText(text, maxWidth);
      totalHeight = lines.length * lineHeight;
    }
    const startY = padding + (maxHeight - totalHeight) / 2;
    lines.forEach((line, index) => {
      ctx.fillText(line, padding, startY + index * lineHeight);
    });
    return canvas.toDataURL();
  }

  // ──────────────────────────────────────────────────────────────────────────
  // FILE UPLOAD SIGNATURE
  // ──────────────────────────────────────────────────────────────────────────

  onFileChange(event: any): void {
    const files = event.target.files;
    if (!files || !files[0]) return;

    this.selectedFile = files[0];
    const fullFileName = files[0].name;
    this.fileName =
      fullFileName.length > 30
        ? fullFileName.substring(0, 30) + '...'
        : fullFileName;

    const fileExtension = files[0].name.split('.').pop().toLowerCase();
    const allowedExtensions = ['jpg', 'jpeg', 'png', 'bmp'];
    if (!allowedExtensions.includes(fileExtension)) {
      this.messageService.add({
        severity: 'warning',
        summary: 'Image Type',
        detail: 'Invalid file type. Please select a JPEG, PNG, or BMP image.',
      });
      event.target.value = '';
      return;
    }
    if (files[0].size / 1024 / 1024 > 10) {
      this.messageService.add({
        severity: 'warning',
        summary: 'Image Type',
        detail: 'File should be less than 10MB in size',
      });
      const el = document.getElementById('upload_file') as HTMLInputElement;
      if (el) el.value = '';
      return;
    }
    if (files[0].name.split('.').length > 2) {
      this.messageService.add({
        severity: 'warning',
        summary: 'Image Type',
        detail: 'File name should have only single extension',
      });
      const el = document.getElementById('upload_file') as HTMLInputElement;
      if (el) el.value = '';
      return;
    }
    if (typeof FileReader !== 'undefined') {
      const reader = new FileReader();
      reader.onload = (e: any) => {
        this.pdfSource = e.target.result;
      };
      reader.readAsDataURL(files[0]);
    }
  }

  clearFile(): void {
    this.selectedFile = '';
    this.fileName = 'Upload Img';
    const el = document.getElementById('upload_file') as HTMLInputElement;
    if (el) el.value = null;
    this.pdfSource = null;
  }

  clearStyle(): void {}

  // ──────────────────────────────────────────────────────────────────────────
  // SAVE / CLEAR IMAGE
  // ──────────────────────────────────────────────────────────────────────────

  clearImage(): void {
    if (this.signOption === 'select') this.clearStyle();
    else if (this.signOption === 'draw') this.clearSignPad();
    else if (this.signOption === 'upload') this.clearFile();
  }

  saveImage(): void {
    if (
      !this.signatoryDetails?.name ||
      this.signatoryDetails.name.trim() === ''
    ) {
      this.openSignatureWarning();
      return;
    }
    if (this.signOption === 'select') this.saveStylingImage();
    else if (this.signOption === 'draw') this.saveSignPad();
    else if (this.signOption === 'upload') {
      if (this.pdfSource) {
        this.saveUploadImage();
      } else {
        this.openSignatureWarning();
      }
    }
    this.sendUserDetails();
  }

  saveSignPad(): void {
    const base64ImageData = this.signPad.toDataURL();
    if (base64ImageData) {
      setTimeout(() => {
        this.getCodedImage(base64ImageData);
      }, 1);
    } else {
      this.openSignatureWarning();
    }
  }

  saveStylingImage(): void {
    this.signImage = this.imageStyle;
    this.getCodedImage(this.signImage as string);
  }

  saveUploadImage(): void {
    this.signImage = this.pdfSource;
    this.getCodedImage(this.signImage);
  }

  async getCodedImage(baseImage: string): Promise<void> {
    // Never submit a stamp left over from an earlier attempt.
    this.stampImage = '';
    const isBlank =
      await this.imageGeneratorService.checkIfImageIsBlank(baseImage);
    if (isBlank) {
      this.signImage = '';
      this.isSignatureValid = false;
      this.openSignatureWarning();
      return;
    }
    const currentDate = new Date();
    const topText = 'Authenticated through Doqfy.in';
    const bottomText =
      'ID:' +
      this.signatoryId +
      ',' +
      currentDate.toLocaleString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      });
    try {
      const modifiedImage = await this.imageGeneratorService.addBordersAndText(
        baseImage,
        topText,
        bottomText,
      );
      // What's placed on the PDF: the same stamp padded to the signature box's
      // shape so it rests on the box bottom, level with Aadhaar, at the same
      // size. signImage stays as the on-screen preview.
      this.stampImage = await this.imageGeneratorService.padToSignatureBox(modifiedImage);
      this.signImage = modifiedImage;
      this.isSignatureValid = true;
      this.signImageChange.emit(this.signImage);
    } catch (error) {
      console.error('Error adding borders and text:', error);
      this.isSignatureValid = false;
    }
  }

  getParallelSignData(): void {
    this.eSignService
      .getExternalSignLink(this.signatoryId, this.orderId)
      .subscribe((res) => {
        if (res['content'] && Object.keys(res['content']).length === 0) {
          this.messageService.add({ severity: 'warn', detail: res['message'] });
          this.eSignService.checkForImageCaptureRefresh(res['message']);
          return;
        }

        if (!res['content'] || !res['content']['sign_url']) {
          const fallbackMsg =
            'Another signatory is currently signing. Please try after 10 minutes.';
          this.messageService.add({
            severity: 'warn',
            detail: res['message'] || fallbackMsg,
          });
          this.eSignService.checkForImageCaptureRefresh(res['message']);
          return;
        }

        if (res['content']['redirect_url']) {
          this.redirecturl = res['content']['redirect_url'];
        }
        const modalElement = document.getElementById('signModel');
        if (modalElement) {
          new bootstrap.Modal(modalElement, {
            backdrop: 'static',
            keyboard: false,
          }).show();
        }
      });
  }

  private openSignatureWarning(): void {
    this.modalService.open(this.signatureWarningTemplate, {
      backdrop: 'static',
      keyboard: false,
    });
  }

  onSignatureWarningOk(modalRef: NgbModalRef): void {
    modalRef.close();
    window.location.reload();
  }

  // ──────────────────────────────────────────────────────────────────────────
  // SUBMIT SIGN
  // ──────────────────────────────────────────────────────────────────────────

  // async SubmitSign(): Promise<void> {
  //   this.spinner.show();
  //   if (this.enableCaptureImage && !this.webcamImage) {
  //     this.messageService.add({
  //       severity: 'warn',
  //       summary: 'Image Required',
  //       detail: 'Image is not captured properly. Please capture image again.'
  //     });
  //     this.spinner.hide();
  //     return;
  //   }
  //   if (
  //     this.positionDetails &&
  //     this.esignId &&
  //     this.signatoryId &&
  //     this.signImage &&
  //     this.userData
  //   ) {
  //     const data: any = {
  //       browser_data: this.userData,
  //       position_data: this.positionDetails,
  //       esign_id: this.esignId,
  //       signatory_id: this.signatoryId,
  //       signature: this.signImage.split(',')[1],
  //       ipAddress: '',
  //       document: ''
  //     };
  //     if (this.orderType === 'CAT_EDIT') {
  //       data.document = this.base64Data?.split(',')[1] ?? '';
  //     }
  //     this.eSignService.sendSignatureDetails(data).subscribe(
  //       (res) => {
  //         if (res) {
  //           setTimeout(() => {
  //             this.signCompleted.emit();
  //             this.handleRedirect();
  //           }, 1000);
  //         }
  //       },
  //       () => {
  //         this.spinner.hide();
  //         this.messageService.add({
  //           severity: 'error',
  //           detail: 'Signature submission failed. Please try again.'
  //         });
  //       }
  //     );
  //   } else {
  //     this.spinner.hide();
  //     this.openSignatureWarning();
  //   }
  // }

  async SubmitSign(): Promise<void> {
    this.spinner.show();

    // STRICT HARD STOP: Agar camera image nahi hai, toh yahin rok do
    if (this.enableCaptureImage && !this.webcamImage) {
      this.messageService.add({
        severity: 'error',
        summary: 'Camera/Image Required',
        detail:
          'Webcam image is mandatory for the audit trail. Please connect a camera or allow permissions.',
      });
      this.spinner.hide();
      return;
    }

    if (
      this.positionDetails &&
      this.esignId &&
      this.signatoryId &&
      this.signImage &&
      this.userData
    ) {
      const data: any = {
        browser_data: this.userData,
        position_data: this.positionDetails,
        esign_id: this.esignId,
        signatory_id: this.signatoryId,
        signature: (this.stampImage || this.signImage).split(',')[1],
        ipAddress: '',
        document: '',
      };
      if (this.orderType === 'CAT_EDIT') {
        data.document = this.base64Data?.split(',')[1] ?? '';
      }

      // SYNCHRONOUS CHAINING: Pehle image verify aur save hone do
      this.eSignService
        .sendUserDetails({
          browser_data: this.userData,
          esign_id: this.esignId,
          signatory_id: this.signatoryId,
          user_image: this.webcamImage,
        })
        .subscribe({
          next: () => {
            // Jab backend bol dega 'Data Saved', tabhi signature submission trigger hoga
            this.eSignService.sendSignatureDetails(data).subscribe({
              next: (res) => {
                if (res) {
                  setTimeout(() => {
                    this.signCompleted.emit();
                    this.handleRedirect();
                  }, 1000);
                }
              },
              error: () => {
                this.spinner.hide();
                this.messageService.add({
                  severity: 'error',
                  detail: 'Signature submission failed. Please try again.',
                });
              },
            });
          },
          error: (err) => {
            // Agar backend se validation fail hoti hai due to missing image
            this.spinner.hide();
            this.messageService.add({
              severity: 'error',
              summary: 'Verification Failed',
              detail:
                err?.error?.message ||
                'Verification failed. Could not capture image/audit data.',
            });
          },
        });
    } else {
      this.spinner.hide();
      this.openSignatureWarning();
    }
  }

  private handleRedirect(): void {
    if (this.redirecturl) {
      this.spinner.hide();
      this.messageService.add({
        severity: 'success',
        detail: 'Process completed. Redirecting to sign...',
      });
      setTimeout(() => {
        if (
          this.urlValidator.isValidRedirectUrl(
            this.redirecturl.toString(),
            DomainType.DOQFY,
          )
        ) {
          window.location.href = this.redirecturl;
        }
      }, 1500);
    } else {
      this.spinner.hide();
      this.messageService.add({
        severity: 'success',
        detail: 'Process completed successfully.',
      });
    }
  }

  // ──────────────────────────────────────────────────────────────────────────
  // USER DETAILS
  // ──────────────────────────────────────────────────────────────────────────

  // sendUserDetails(): void {
  //   if (!this.userData) return;
  //   if (this.enableCaptureImage && !this.webcamImage) {
  //     this.messageService.add({
  //       severity: 'warn',
  //       summary: 'Image Required',
  //       detail: 'Image is not captured properly. Please capture image again.',
  //     });
  //     return;
  //   }
  //   this.eSignService
  //     .sendUserDetails({
  //       browser_data: this.userData,
  //       esign_id: this.esignId,
  //       signatory_id: this.signatoryId,
  //       user_image: this.webcamImage,
  //     })
  //     .subscribe(() => {});
  // }

  sendUserDetails(): void {
  if (!this.userData) return;
  if (this.enableCaptureImage && !this.webcamImage) {
    this.messageService.add({
      severity: 'warn',
      summary: 'Image Required',
      detail: 'Image is not captured properly. Please capture image again.',
    });
    return;
  }
  this.eSignService
    .sendUserDetails({
      browser_data: this.userData,
      esign_id: this.esignId,
      signatory_id: this.signatoryId,
    })
    .subscribe({
      next: (res) => {
        console.log('[Audit Trail] Browser configuration data synced successfully.');
      },
      error: (err) => {
        console.error('[Audit Trail] Error updating browser profile:', err);
        this.messageService.add({
          severity: 'error',
          summary: 'Sync Failure',
          detail: err?.error?.message || 'Failed to update browser configuration profile.',
        });
      }
    });
}

  startEmailCooldown(): void {
    this.emailCooldownTime = 10;
    this.emailCooldownInterval = setInterval(() => {
      this.emailCooldownTime--;
      if (this.emailCooldownTime <= 0) {
        this.isEmailButtonDisabled = false;
        clearInterval(this.emailCooldownInterval);
      }
    }, 1000);
  }
}
